import { useEffect, useState } from "react"
import { Container, createStyles, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, withStyles } from "@material-ui/core"
import Paper from "@material-ui/core/Paper"
import { NavLink } from "react-router-dom"
import { URL_FOR_API } from "../../constants/constants"
import actionsWithApi from "../../customHooks/actionsWithApi"
import dateFormatting from "../../scripts/dateFormatting"
import currencyFormatting from "../../scripts/currencyFormatting"
import Preloader from "../shared/Preloader/Preloader"
import s from "./ContractMismatchPage.module.scss"

const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
        },
        body: {
            fontSize: 14,
        },
    }),
)(TableCell)

const StyledTableRow = withStyles((theme: Theme) =>
    createStyles({
        root: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.action.hover,
            },
        },
    }),
)(TableRow)

type MismatchItem = {
    contractId: number
    caseId: string
    createDate: string
    contractStatus: string
    totalAmount: number
    requestsCount: number
    totalChecks: number
    checksCount: number
    possibleProblem: string
}

const openLink = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
}

export default function ContractMismatchPage() {
    const [items, setItems] = useState<MismatchItem[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await actionsWithApi("GET", URL_FOR_API + "/api/v1/Contract/mismatch-list", null)
                setItems(response.data)
            } catch (error) {
                console.error(error.message)
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [])

    if (isLoading) {
        return <Preloader size="100" position="center" />
    }

    return (
        <Container maxWidth="lg" className={s["ContractMismatchPage"]}>
            <h1 className={s["ContractMismatchPage-Title"]}>Contracts with Amount Mismatch</h1>

            {items.length === 0 ? (
                <h2 style={{ textAlign: "center" }}>No mismatches found</h2>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Case ID</StyledTableCell>
                                <StyledTableCell align="center">Date Created</StyledTableCell>
                                <StyledTableCell align="center">Status</StyledTableCell>
                                <StyledTableCell align="center">Total Amount</StyledTableCell>
                                <StyledTableCell align="center">Requests Count</StyledTableCell>
                                <StyledTableCell align="center">Total Checks</StyledTableCell>
                                <StyledTableCell align="center">Checks Count</StyledTableCell>
                                <StyledTableCell align="center">Possible Problem</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <StyledTableRow key={item.contractId}>
                                    <StyledTableCell component="th" scope="row">
                                        <NavLink to={`contract-page?${item.contractId}`} onClick={openLink}>
                                            {item.caseId}
                                        </NavLink>
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {dateFormatting(item.createDate)}
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {item.contractStatus?.split(/(?=[A-Z])/)[0]}
                                        <br />
                                        {item.contractStatus?.split(/(?=[A-Z])/)[1]}
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {currencyFormatting(item.totalAmount)}
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {item.requestsCount}
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {currencyFormatting(item.totalChecks)}
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {item.checksCount}
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        <span className={s["ContractMismatchPage-Problem"]}>
                                            {item.possibleProblem}
                                        </span>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    )
}
